import { useAuthStore } from '../stores/authStore';

/**
 * API error response type
 */
export interface ApiError {
  error: string;
  message: string;
  status: number;
  details?: unknown;
}

/**
 * Custom error class for API errors
 */
export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly errorCode: string;
  public readonly details?: unknown;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.status = error.status;
    this.errorCode = error.error;
    this.details = error.details;
  }
}

/**
 * API base URL - uses relative path for same-origin requests
 */
const API_BASE = '/api';

/**
 * HTTP methods that require CSRF token
 */
const STATE_CHANGING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Build request headers including CSRF token for state-changing requests
 * 
 * Requirements: 11.2 - Include CSRF token in all state-changing requests
 */
function buildHeaders(method: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Include CSRF token for state-changing requests
  if (STATE_CHANGING_METHODS.includes(method.toUpperCase())) {
    const csrfToken = useAuthStore.getState().csrfToken;
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  return headers;
}

/**
 * Handle API response and parse JSON
 */
async function handleResponse<T>(response: Response): Promise<T> {
  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401) {
    const { logout } = useAuthStore.getState();
    logout();
    
    // Redirect to login page
    window.location.href = '/login';
    
    throw new ApiRequestError({
      error: 'unauthorized',
      message: 'Session expired. Please log in again.',
      status: 401,
    });
  }

  // Parse response body
  let body: unknown;
  const contentType = response.headers.get('content-type');
  
  if (contentType?.includes('application/json')) {
    body = await response.json();
  } else {
    body = await response.text();
  }

  // Handle error responses
  if (!response.ok) {
    const error = body as ApiError;
    throw new ApiRequestError({
      error: error.error || 'unknown_error',
      message: error.message || 'An unexpected error occurred',
      status: response.status,
      details: error.details,
    });
  }

  return body as T;
}

/**
 * Generic fetch wrapper with error handling
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const method = options.method || 'GET';
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    method,
    headers: {
      ...buildHeaders(method),
      ...options.headers,
    },
    credentials: 'include', // Include cookies for session
  });

  return handleResponse<T>(response);
}

/**
 * API client with typed methods for each HTTP verb
 */
export const api = {
  /**
   * GET request
   */
  get: <T>(endpoint: string, options?: RequestInit): Promise<T> =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  /**
   * POST request
   */
  post: <T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    const body = data ? JSON.stringify(data) : null;
    return request<T>(endpoint, {
      ...options,
      method: 'POST',
      ...(body !== null && { body }),
    });
  },

  /**
   * PATCH request
   */
  patch: <T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    const body = data ? JSON.stringify(data) : null;
    return request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      ...(body !== null && { body }),
    });
  },

  /**
   * DELETE request
   */
  delete: <T>(endpoint: string, options?: RequestInit): Promise<T> =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

/**
 * Auth API endpoints
 */
export const authApi = {
  login: (email: string, password: string, totpCode?: string) =>
    api.post<{ 
      admin?: { id: string; email: string; role: 'admin' | 'super_admin'; lastLoginAt: string | null; mustChangePassword: boolean; totpEnabled: boolean }; 
      csrfToken?: string;
      totpRequired?: boolean;
      message?: string;
    }>(
      '/auth/login',
      { email, password, totpCode }
    ),

  logout: () => api.post<void>('/auth/logout'),

  me: () =>
    api.get<{ id: string; email: string; role: 'admin' | 'super_admin'; lastLoginAt: string | null; totpEnabled: boolean }>(
      '/auth/me'
    ),

  changePassword: (currentPassword: string | undefined, newPassword: string) =>
    api.post<{ success: boolean; message: string }>(
      '/auth/change-password',
      { currentPassword, newPassword }
    ),
  
  // TOTP endpoints
  totp: {
    setup: () =>
      api.post<{ secret: string; uri: string; qrCodeDataUrl: string }>('/auth/totp/setup'),
    
    verify: (code: string) =>
      api.post<{ success: boolean; message: string }>('/auth/totp/verify', { code }),
    
    disable: (code: string) =>
      api.post<{ success: boolean; message: string }>('/auth/totp/disable', { code }),
    
    status: () =>
      api.get<{ enabled: boolean }>('/auth/totp/status'),
  },
};

/**
 * Users API endpoints
 */
export const usersApi = {
  list: (params?: { page?: number; pageSize?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return api.get<{
      data: Array<{
        id: string;
        email: string;
        plan: string;
        isActive: boolean;
        createdAt: string;
        lastLoginAt: string | null;
        agentCount: number;
        totpEnabled: boolean;
        phoneVerified: boolean;
      }>;
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/users${query ? `?${query}` : ''}`);
  },

  get: async (id: string) => {
    const response = await api.get<{
      user: {
        id: string;
        email: string;
        plan: string;
        is_active: boolean;
        created_at: string;
        last_login_at: string | null;
        agent_count: number;
        totp_enabled: boolean;
        phone_verified: boolean;
      };
      agents: Array<{
        id: string;
        subdomain: string;
        label: string | null;
        is_active: boolean;
        is_online: boolean;
        platform: string | null;
        agent_version: string | null;
        last_seen_at: string | null;
        created_at: string;
      }>;
      sessions: Array<{
        id: string;
        ip_address: string | null;
        user_agent: string | null;
        created_at: string;
        expires_at: string;
        last_refreshed_at: string | null;
      }>;
    }>(`/users/${id}`);
    // Transform snake_case to camelCase for frontend
    return {
      user: {
        id: response.user.id,
        email: response.user.email,
        plan: response.user.plan,
        isActive: response.user.is_active,
        createdAt: response.user.created_at,
        lastLoginAt: response.user.last_login_at,
        agentCount: response.user.agent_count,
        totpEnabled: response.user.totp_enabled,
        phoneVerified: response.user.phone_verified,
      },
      agents: response.agents.map(a => ({
        id: a.id,
        subdomain: a.subdomain,
        label: a.label,
        isActive: a.is_active,
        isOnline: a.is_online,
        platform: a.platform,
        agentVersion: a.agent_version,
        lastSeenAt: a.last_seen_at,
        createdAt: a.created_at,
      })),
      sessions: response.sessions.map(s => ({
        id: s.id,
        ipAddress: s.ip_address,
        userAgent: s.user_agent,
        createdAt: s.created_at,
        expiresAt: s.expires_at,
        lastRefreshedAt: s.last_refreshed_at,
      })),
    };
  },

  update: (id: string, data: { plan?: string; isActive?: boolean }) =>
    api.patch<void>(`/users/${id}`, data),

  getSessions: (id: string) =>
    api.get<Array<{
      id: string;
      ipAddress: string | null;
      userAgent: string | null;
      createdAt: string;
      lastRefreshedAt: string | null;
    }>>(`/users/${id}/sessions`),

  getRateLimits: (id: string) =>
    api.get<Array<{
      key: string;
      category: string;
      count: number;
      limit: number;
      ttl: number;
    }>>(`/users/${id}/rate-limits`),

  clearRateLimits: (id: string) =>
    api.delete<void>(`/users/${id}/rate-limits`),
};

/**
 * Agents API endpoints
 */
export const agentsApi = {
  list: (params?: {
    page?: number;
    pageSize?: number;
    isOnline?: boolean;
    platform?: string;
    userId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.isOnline !== undefined) searchParams.set('isOnline', params.isOnline.toString());
    if (params?.platform) searchParams.set('platform', params.platform);
    if (params?.userId) searchParams.set('userId', params.userId);
    const query = searchParams.toString();
    return api.get<{
      data: Array<{
        id: string;
        userId: string;
        userEmail: string;
        subdomain: string;
        label: string | null;
        isActive: boolean;
        isOnline: boolean;
        platform: string | null;
        agentVersion: string | null;
        lastSeenAt: string | null;
        createdAt: string;
      }>;
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/agents${query ? `?${query}` : ''}`);
  },

  get: (id: string) =>
    api.get<{
      id: string;
      userId: string;
      userEmail: string;
      subdomain: string;
      label: string | null;
      isActive: boolean;
      isOnline: boolean;
      platform: string | null;
      agentVersion: string | null;
      lastSeenAt: string | null;
      createdAt: string;
    }>(`/agents/${id}`),

  update: (id: string, data: { isActive?: boolean }) =>
    api.patch<void>(`/agents/${id}`, data),

  regenerateToken: (id: string) =>
    api.post<{ token: string }>(`/agents/${id}/token`),
};

/**
 * Sessions API endpoints
 */
export const sessionsApi = {
  list: (params?: {
    page?: number;
    pageSize?: number;
    userId?: string;
    userEmail?: string;
    ipAddress?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.userEmail) searchParams.set('userEmail', params.userEmail);
    if (params?.ipAddress) searchParams.set('ipAddress', params.ipAddress);
    const query = searchParams.toString();
    return api.get<{
      data: Array<{
        id: string;
        userId: string;
        userEmail: string;
        ipAddress: string | null;
        userAgent: string | null;
        createdAt: string;
        expiresAt: string;
        lastRefreshedAt: string | null;
        isRevoked: boolean;
      }>;
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/sessions${query ? `?${query}` : ''}`);
  },

  revoke: (id: string) => api.delete<void>(`/sessions/${id}`),
};

/**
 * Rate limits API endpoints
 */
export const rateLimitsApi = {
  search: async (pattern: string) => {
    const response = await api.get<{
      data: Array<{
        key: string;
        category: string;
        count: number;
        limit: number;
        ttl: number;
      }>;
    }>(`/rate-limits?pattern=${encodeURIComponent(pattern)}`);
    return response.data;
  },

  clear: (key: string) =>
    api.delete<void>(`/rate-limits/${encodeURIComponent(key)}`),
};

/**
 * Audit logs API endpoints
 */
export const auditLogsApi = {
  list: (params?: {
    page?: number;
    pageSize?: number;
    eventType?: string;
    eventStatus?: string;
    userId?: string;
    ipAddress?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.eventType) searchParams.set('eventType', params.eventType);
    if (params?.eventStatus) searchParams.set('eventStatus', params.eventStatus);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.ipAddress) searchParams.set('ipAddress', params.ipAddress);
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    const query = searchParams.toString();
    return api.get<{
      data: Array<{
        id: number;
        eventType: string;
        eventStatus: string;
        userId: string | null;
        userEmail: string | null;
        ipAddress: string | null;
        userAgent: string | null;
        details: Record<string, unknown>;
        errorMessage: string | null;
        requestId: string | null;
        createdAt: string;
      }>;
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/audit-logs${query ? `?${query}` : ''}`);
  },

  export: (params?: {
    eventType?: string;
    eventStatus?: string;
    userId?: string;
    ipAddress?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.eventType) searchParams.set('eventType', params.eventType);
    if (params?.eventStatus) searchParams.set('eventStatus', params.eventStatus);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.ipAddress) searchParams.set('ipAddress', params.ipAddress);
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    const query = searchParams.toString();
    // Return the URL for download
    return `${API_BASE}/audit-logs/export${query ? `?${query}` : ''}`;
  },
};

/**
 * Health API endpoints
 */
export const healthApi = {
  get: () =>
    api.get<{
      services: Array<{
        service: string;
        status: string;
        lastChecked: string;
        responseTime: number | null;
        details: Record<string, unknown>;
      }>;
      metrics: {
        totalUsers: number;
        totalAgents: number;
        onlineAgents: number;
        activeSessions: number;
      };
    }>('/health'),
};

/**
 * Admins API endpoints
 */
export const adminsApi = {
  list: async () => {
    const response = await api.get<{
      data: Array<{
        id: string;
        email: string;
        role: string;
        isActive: boolean;
        mustChangePassword: boolean;
        createdAt: string;
        updatedAt: string;
        lastLoginAt: string | null;
      }>;
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>('/admins');
    return response.data;
  },

  create: (email: string) =>
    api.post<{ admin: { id: string; email: string; role: string; isActive: boolean; createdAt: string; lastLoginAt: string | null }; temporaryPassword: string }>('/admins', { email }),

  get: (id: string) =>
    api.get<{
      id: string;
      email: string;
      role: string;
      isActive: boolean;
      createdAt: string;
      lastLoginAt: string | null;
    }>(`/admins/${id}`),

  update: (id: string, data: { role?: string; isActive?: boolean }) =>
    api.patch<void>(`/admins/${id}`, data),
};

/**
 * Commands API endpoints
 */
export const commandsApi = {
  forceLogout: (userId: string) =>
    api.post<{ success: boolean; sessionsRevoked: number }>(
      '/commands/force-logout',
      { userId }
    ),

  clearRateLimits: (target: { userId?: string; ipAddress?: string }) =>
    api.post<{ success: boolean; keysCleared: number }>(
      '/commands/clear-rate-limits',
      target
    ),

  regenerateToken: (agentId: string) =>
    api.post<{ token: string }>('/commands/regenerate-token', { agentId }),
};

/**
 * Beta Access API endpoints
 */
export interface EarlyAccessRequest {
  id: number;
  name: string;
  email: string;
  company: string | null;
  usecase: string;
  createdAt: string;
  status: 'pending' | 'approved' | 'denied';
  processedAt: string | null;
  processedBy: string | null;
}

export const betaAccessApi = {
  list: (params?: { page?: number; pageSize?: number; status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return api.get<{
      data: EarlyAccessRequest[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/beta-access${query ? `?${query}` : ''}`);
  },

  approve: (id: number) =>
    api.post<{ success: boolean; message: string; emailSent: boolean }>(
      `/beta-access/${id}/approve`
    ),

  deny: (id: number, options?: { reason?: string; sendEmail?: boolean }) =>
    api.post<{ success: boolean; message: string; emailSent: boolean }>(
      `/beta-access/${id}/deny`,
      options
    ),
};
